import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";
import { usePageMeta, useNoIndex } from "@/lib/seo";
import { Mail, Lock, User, CheckCircle2, Copy, ExternalLink } from "lucide-react";

export const inputCls = "w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A62A]";
export const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5 block";

export function AuthShell({ title, subtitle, children }) {
  useNoIndex();
  return (
    <div className="min-h-[70vh] bg-[#F9FAFB] py-20">
      <div className="max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <span className="mk-gold-line mb-5 inline-block" />
          <h1 className="font-serif-display text-3xl text-[#0A2E4D]">{title}</h1>
          {subtitle && <p className="text-sm text-[#4B5563] mt-2">{subtitle}</p>}
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">{children}</div>
      </div>
    </div>
  );
}

export function GoogleButton({ label = "Continue with Google" }) {
  const login = () => {
    const redirect = `${window.location.origin}/account`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <button type="button" onClick={login} data-testid="google-signin-btn"
      className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] px-5 py-3 text-sm font-semibold text-[#1F2937] transition-colors">
      <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden>
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
      </svg>
      {label}
    </button>
  );
}

export function DevLinkNotice({ link, kind }) {
  if (!link) return null;
  let safeLink = link;
  try {
    const u = new URL(link);
    safeLink = `${window.location.origin}${u.pathname}${u.search}`;
  } catch {}
  return (
    <div className="mt-5 bg-[#FDF8EC] border border-[#D4A62A]/40 rounded-xl p-4 text-left" data-testid={`dev-${kind}-link`}>
      <p className="text-xs font-bold text-[#BA901D] uppercase tracking-wider mb-1.5">Dev mode — email sending not configured yet</p>
      <p className="text-xs text-[#4B5563] mb-2.5">Use this link to continue (in production this arrives by email):</p>
      <div className="flex items-center gap-2">
        <a href={safeLink} className="flex-1 text-xs text-[#0A2E4D] underline break-all inline-flex items-start gap-1" data-testid={`dev-${kind}-anchor`}>{safeLink}</a>
        <button type="button" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }}
          className="shrink-0 p-2 rounded-lg border border-[#E5E7EB] text-[#4B5563] hover:text-[#0A2E4D]" aria-label="Copy link"><Copy size={13} /></button>
      </div>
    </div>
  );
}

export default function ClientLogin() {
  usePageMeta("Sign In", "Sign in to your Makchester Tours & Travel account to track your visa applications, enquiries and bookings.");
  const navigate = useNavigate();
  const loc = useLocation();
  const { setClient } = useClientAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/client/login", f);
      setClient(r.data.user);
      toast.success(`Welcome back, ${r.data.user.name.split(" ")[0]}!`);
      navigate(loc.state?.from || "/account");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/client/forgot-password", { email: f.email });
      toast.success(r.data.message);
      setDevLink(r.data.dev_reset_link || null);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title={forgot ? "Reset your password" : "Welcome back"} subtitle={forgot ? "Enter your email and we'll send you a reset link." : "Sign in to track your visa applications and bookings."}>
      <form onSubmit={forgot ? sendReset : submit} className="space-y-4" data-testid="client-login-form">
        <div>
          <label className={labelCls}><Mail size={11} className="inline mr-1" /> Email</label>
          <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="client-email-input" />
        </div>
        {!forgot && (
          <div>
            <label className={labelCls}><Lock size={11} className="inline mr-1" /> Password</label>
            <input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="client-password-input" />
          </div>
        )}
        <button type="submit" disabled={busy} className="mk-btn-gold w-full !py-3.5 disabled:opacity-60" data-testid="client-login-submit">
          {busy ? "Please wait…" : forgot ? "Send Reset Link" : "Sign In"}
        </button>
        <DevLinkNotice link={devLink} kind="reset" />
        <button type="button" onClick={() => { setForgot(!forgot); setDevLink(null); }} className="w-full text-xs text-[#4B5563] hover:text-[#0A2E4D] underline" data-testid="client-forgot-toggle">
          {forgot ? "← Back to sign in" : "Forgot your password?"}
        </button>
      </form>
      {!forgot && (
        <>
          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-[#E5E7EB]" /><span className="text-xs text-[#9CA3AF]">or</span><div className="flex-1 h-px bg-[#E5E7EB]" /></div>
          <GoogleButton />
          <p className="text-center text-xs text-[#4B5563] mt-6">New to Makchester? <Link to="/account/register" className="text-[#0A2E4D] font-semibold underline" data-testid="goto-register-link">Create an account</Link></p>
        </>
      )}
    </AuthShell>
  );
}

export function ClientRegister() {
  usePageMeta("Create Account", "Create your Makchester Tours & Travel account to track visa applications, enquiries and bookings in one place.");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/client/register", f);
      setDone(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <AuthShell title="Check your email" subtitle={`We've created your account for ${f.email}.`}>
        <div className="text-center" data-testid="register-success">
          <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
          <p className="text-sm text-[#4B5563] mt-4">{done.email_sent ? "A verification link has been sent to your inbox. Click it to activate your account." : "Your account was created — verify your email to activate it."}</p>
          <DevLinkNotice link={done.dev_verification_link} kind="verify" />
          <Link to="/account/login" className="mk-btn-navy w-full !py-3 mt-6">Go to Sign In</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Track your visa applications, documents and bookings in one place.">
      <form onSubmit={submit} className="space-y-4" data-testid="client-register-form">
        <div><label className={labelCls}><User size={11} className="inline mr-1" /> Full Name</label>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="register-name-input" /></div>
        <div><label className={labelCls}><Mail size={11} className="inline mr-1" /> Email</label>
          <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="register-email-input" /></div>
        <div><label className={labelCls}><Lock size={11} className="inline mr-1" /> Password (min 8 characters)</label>
          <input type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="register-password-input" /></div>
        <button type="submit" disabled={busy} className="mk-btn-gold w-full !py-3.5 disabled:opacity-60" data-testid="register-submit-btn">{busy ? "Creating account…" : "Create Account"}</button>
      </form>
      <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-[#E5E7EB]" /><span className="text-xs text-[#9CA3AF]">or</span><div className="flex-1 h-px bg-[#E5E7EB]" /></div>
      <GoogleButton label="Sign up with Google" />
      <p className="text-center text-xs text-[#4B5563] mt-6">Already have an account? <Link to="/account/login" className="text-[#0A2E4D] font-semibold underline">Sign in</Link></p>
    </AuthShell>
  );
}

export function ClientVerify() {
  usePageMeta("Verify Email");
  const [sp] = useSearchParams();
  const [state, setState] = useState({ status: "verifying", message: "" });
  const ran = React.useRef(false);
  React.useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.get(`/client/verify?token=${sp.get("token") || ""}`)
      .then((r) => setState({ status: "ok", message: r.data.message }))
      .catch((e) => setState({ status: "error", message: formatApiErrorDetail(e?.response?.data?.detail) }));
  }, [sp]);
  return (
    <AuthShell title="Email verification">
      <div className="text-center" data-testid="verify-result">
        {state.status === "verifying" && <p className="text-sm text-[#4B5563]">Verifying your email…</p>}
        {state.status === "ok" && (
          <>
            <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
            <p className="text-sm text-[#4B5563] mt-4" data-testid="verify-success-message">{state.message}</p>
            <Link to="/account/login" className="mk-btn-gold w-full !py-3 mt-6" data-testid="verify-goto-login">Sign In Now</Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <p className="text-sm text-red-500" data-testid="verify-error-message">{state.message}</p>
            <Link to="/account/register" className="mk-btn-navy w-full !py-3 mt-6">Register Again</Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}

export function ClientReset() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/client/reset-password", { token: sp.get("token") || "", password });
      toast.success(r.data.message);
      navigate("/account/login");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4" data-testid="client-reset-form">
        <div><label className={labelCls}><Lock size={11} className="inline mr-1" /> New Password (min 8 characters)</label>
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password-input" /></div>
        <button type="submit" disabled={busy} className="mk-btn-gold w-full !py-3.5 disabled:opacity-60" data-testid="reset-submit-btn">{busy ? "Updating…" : "Update Password"}</button>
      </form>
    </AuthShell>
  );
}
